import React, { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import { Key, Plus, Trash2, Copy, Check, AlertTriangle, Clock, Shield } from 'lucide-react';
import { buttonStyles } from '../utils/colors';

const SCOPE_LABELS = {
  'forms:read': 'Read Forms',
  'forms:write': 'Write Forms',
  'responses:read': 'Read Responses',
  'responses:write': 'Write Responses',
  'analytics:read': 'Read Analytics',
};

const SCOPE_DESCRIPTIONS = {
  'forms:read': 'View forms and their configuration',
  'forms:write': 'Create, update, and delete forms',
  'responses:read': 'View form submissions and responses',
  'responses:write': 'Submit form responses',
  'analytics:read': 'View form analytics and session data',
};

const ApiKeysTab = ({ organization }) => {
  const { showSuccess, showError } = useToast();
  const [apiKeys, setApiKeys] = useState([]);
  const [availableScopes, setAvailableScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    scopes: ['forms:read', 'responses:read', 'analytics:read'],
  });
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchApiKeys = useCallback(async () => {
    try {
      const response = await apiRequest('/api-keys/');
      if (response.ok) {
        const keys = await response.json();
        setApiKeys(keys);
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to fetch API keys');
      }
    } catch (error) {
      showError('Error fetching API keys');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const fetchAvailableScopes = useCallback(async () => {
    try {
      const response = await apiRequest('/api-keys/scopes');
      if (response.ok) {
        const data = await response.json();
        setAvailableScopes(data.scopes || []);
      }
    } catch (error) {
      console.error('Error fetching scopes:', error);
    }
  }, []);

  useEffect(() => {
    fetchApiKeys();
    fetchAvailableScopes();
  }, [fetchApiKeys, fetchAvailableScopes]);

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) {
      showError('Please enter a name for the API key');
      return;
    }
    if (createForm.scopes.length === 0) {
      showError('Please select at least one scope');
      return;
    }

    setCreating(true);
    try {
      const response = await apiRequest('/api-keys/', {
        method: 'POST',
        body: JSON.stringify(createForm),
      });

      if (response.ok) {
        const key = await response.json();
        setNewKey(key);
        setShowCreateModal(false);
        setShowKeyModal(true);
        setCreateForm({
          name: '',
          scopes: ['forms:read', 'responses:read', 'analytics:read'],
        });
        fetchApiKeys();
        showSuccess('API key created successfully');
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to create API key');
      }
    } catch (error) {
      showError('Error creating API key');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    setDeletingId(keyId);
    try {
      const response = await apiRequest(`/api-keys/${keyId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setApiKeys(apiKeys.filter(key => key.id !== keyId));
        showSuccess('API key revoked successfully');
      } else {
        const error = await response.json();
        showError(error.detail || 'Failed to revoke API key');
      }
    } catch (error) {
      showError('Error revoking API key');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyKey = async () => {
    if (newKey?.api_key) {
      await navigator.clipboard.writeText(newKey.api_key);
      setCopiedKey(true);
      showSuccess('API key copied to clipboard');
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const handleScopeToggle = (scope) => {
    setCreateForm(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMaskedKey = (prefix, lastFour) => {
    return `${prefix}${'•'.repeat(24)}${lastFour}`;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">API Keys</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage API keys for programmatic access to your organization's data.
        </p>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl p-4">
        <div className="flex gap-3">
          <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">API Key Security</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300">
              <li>API keys grant access to your organization's data via the API</li>
              <li>Keys are shown only once when created - store them securely</li>
              <li>Rate limited to 500 requests per day per key</li>
              <li>Revoke keys immediately if compromised</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Create Key Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowCreateModal(true)}
          className={`${buttonStyles.primary} flex items-center gap-2`}
        >
          <Plus className="h-4 w-4" />
          Create API Key
        </button>
      </div>

      {/* API Keys List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Your API Keys
            </h3>
            <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
              ({apiKeys.length})
            </span>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            Loading API keys...
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="p-8 text-center">
            <Key className="h-8 w-8 text-gray-200 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No API keys yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {apiKeys.map((key) => (
              <div key={key.id} className="px-6 py-4 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        {key.name}
                      </h4>
                      {!key.is_active && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                          Revoked
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-sm text-gray-500 dark:text-gray-400 mb-2">
                      {getMaskedKey(key.key_prefix, key.last_four)}
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {key.scopes.map((scope) => (
                        <span
                          key={scope}
                          className="px-2 py-0.5 text-xs rounded-full bg-brand-100 dark:bg-brand-900 text-brand-800 dark:text-brand-200"
                        >
                          {SCOPE_LABELS[scope] || scope}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Created: {formatDate(key.created_at)}
                      </span>
                      {key.last_used_at && (
                        <span className="flex items-center gap-1">
                          Last used: {formatDate(key.last_used_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteKey(key.id)}
                    disabled={deletingId === key.id}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Revoke API key"
                  >
                    {deletingId === key.id ? (
                      <span className="animate-spin">...</span>
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="absolute inset-0" onClick={() => setShowCreateModal(false)} />
          <div className="relative z-10 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Create API Key
              </h3>
            </div>
            <form onSubmit={handleCreateKey}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Key Name
                  </label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="e.g., Zapier Integration"
                    className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    A descriptive name to help you identify this key
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Permissions
                  </label>
                  <div className="space-y-2">
                    {availableScopes.map((scope) => (
                      <label
                        key={scope}
                        className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={createForm.scopes.includes(scope)}
                          onChange={() => handleScopeToggle(scope)}
                          className="mt-0.5 h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 dark:border-gray-600 rounded"
                        />
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                            {SCOPE_LABELS[scope] || scope}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {SCOPE_DESCRIPTIONS[scope]}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className={buttonStyles.secondary}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className={buttonStyles.primary}
                >
                  {creating ? 'Creating...' : 'Create API Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Key Display Modal */}
      {showKeyModal && newKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="relative z-10 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                API Key Created
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <p className="font-medium">Save this key now!</p>
                    <p className="text-amber-700 dark:text-amber-300">
                      This is the only time you'll see the full API key. Store it securely.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Your API Key
                </label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-gray-100 dark:bg-gray-900 rounded-lg font-mono text-sm text-gray-900 dark:text-gray-100 break-all">
                    {newKey.api_key}
                  </code>
                  <button
                    onClick={handleCopyKey}
                    className={`p-3 rounded-lg border transition-colors ${
                      copiedKey
                        ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800 text-brand-600 dark:text-brand-400'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    title="Copy to clipboard"
                  >
                    {copiedKey ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Usage Example
                </label>
                <pre className="p-3 bg-gray-900 dark:bg-gray-950 rounded-lg font-mono text-sm text-gray-100 overflow-x-auto">
{`curl -H "X-API-Key: ${newKey.api_key}" \\
  https://api.thefastestform.com/api/forms/`}
                </pre>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => {
                  setShowKeyModal(false);
                  setNewKey(null);
                }}
                className={buttonStyles.primary}
              >
                I've saved my key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeysTab;
